import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  toApiError,
  useApi,
  type ChannexQuarantinedRevision,
  type ChannexRevisionReplayResult,
} from '@/shared/api';
import { replayFailureText, type SettledHold } from './revisionHoldPresentation';
import { REVISION_HOLDS_KEY } from './useRevisionHolds';

export type ReplayRowState =
  | { kind: 'running' }
  | { kind: 'done'; result: ChannexRevisionReplayResult }
  | { kind: 'failed'; message: string };

/**
 * „Erneut verarbeiten" je Zeile. Der Zustand gilt erst, wenn der Server geantwortet hat. Nach
 * jeder Antwort lädt die Liste neu: eine angewendete Revision kann spätere derselben Buchung
 * wieder warten lassen, und ein Fehlschlag zählt seinen Versuch. Eine übernommene Zeile bleibt
 * dabei mit ihrem Ergebnis an ihrem Platz stehen (`settled`), obwohl der Server sie nicht mehr
 * liefert — sonst verschwände die Bestätigung mit der Zeile.
 */
export function useRevisionReplay() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [states, setStates] = useState<Record<string, ReplayRowState>>({});
  const [settled, setSettled] = useState<SettledHold[]>([]);
  const targets = useRef(new Map<string, SettledHold>());
  const set = (id: string, state: ReplayRowState) =>
    setStates((current) => ({ ...current, [id]: state }));
  const refreshList = () => queryClient.invalidateQueries({ queryKey: REVISION_HOLDS_KEY });

  const mutation = useMutation({
    mutationFn: (id: string) => api.channex.replayQuarantinedRevision(id),
    // Offline pausiert TanStack sonst still, und „Wird verarbeitet …" endete nie.
    networkMode: 'always',
    onMutate: (id) => set(id, { kind: 'running' }),
    onSuccess: async (result, id) => {
      set(id, { kind: 'done', result });
      const target = targets.current.get(id);
      if (target && result.outcome !== 'StillQuarantined') {
        setSettled((current) => [...current.filter((entry) => entry.row.id !== id), target]);
      }
      const keys =
        result.outcome === 'StillQuarantined' ? [] : [['bookings'], ['operations'], ['calendar']];
      await Promise.all([
        refreshList(),
        ...keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      ]);
    },
    onError: async (error, id) => {
      const problem = await toApiError(error);
      set(id, { kind: 'failed', message: replayFailureText(problem) });
      await refreshList();
    },
  });

  return {
    replay: (row: ChannexQuarantinedRevision, index: number) => {
      targets.current.set(row.id, { row, index });
      mutation.mutate(row.id);
    },
    stateOf: (id: string): ReplayRowState | undefined => states[id],
    settled,
  };
}
